import { PersonAffiliation, PersonOwnershipPeriod, PersonRole } from '../types'
import {
  companyCountryFlagCode,
  formatOwnershipStakeLabels,
  formatPersonRoleLabel,
} from './format'

export type PersonActivityKind = 'in' | 'out' | 'stake_change'

export interface PersonActivityRow {
  kind: PersonActivityKind
  date: string
  companyCvr: string
  companyName: string
  /** ISO country code when the affiliated company is abroad. */
  companyCountryCode?: string | null
  roleLabel: string
  organizationType: string
  /** Compact stake for inline display (matches company Ejere strip). */
  stakeLabel: string | null
  /** Full-precision stake for hover title when it differs from compact. */
  stakeTitle: string | null
  previousStakeLabel: string | null
  previousStakeTitle: string | null
}

const DEFAULT_LIMIT = 5

function stakeFingerprint(
  percent: number | null | undefined,
  percentMax: number | null | undefined,
  underFive?: boolean
): string {
  return `${percent ?? ''}|${percentMax ?? ''}|${underFive ? '1' : '0'}`
}

function formatRoleStake(
  role: PersonRole,
): { compact: string; full: string } | null {
  const isLegalRegister = role.organizationType === 'EJERREGISTER'
  if (!isLegalRegister && role.ownershipCapitalPercent == null) return null
  return formatOwnershipStakeLabels(
    role.ownershipCapitalPercent,
    role.ownershipCapitalPercentMax,
    isLegalRegister ? role.ownershipRegistrationUnderFive : false,
  )
}

function formatPeriodStake(
  period: PersonOwnershipPeriod,
): { compact: string; full: string } | null {
  const isLegalRegister = period.organizationType === 'EJERREGISTER'
  if (!isLegalRegister && period.ownershipCapitalPercent == null) return null
  return formatOwnershipStakeLabels(
    period.ownershipCapitalPercent,
    period.ownershipCapitalPercentMax,
    isLegalRegister ? period.ownershipRegistrationUnderFive : false,
  )
}

function stakeFields(labels: { compact: string; full: string } | null): {
  stakeLabel: string | null
  stakeTitle: string | null
} {
  if (!labels) return { stakeLabel: null, stakeTitle: null }
  return {
    stakeLabel: labels.compact,
    stakeTitle: labels.full !== labels.compact ? labels.full : null,
  }
}

function compareIsoDatesDesc(a: string, b: string): number {
  const aKey = a.split('T')[0]
  const bKey = b.split('T')[0]
  if (aKey === bKey) return 0
  return aKey < bKey ? 1 : -1
}

/** YYYY from an ISO date string. */
export function activityYear(date: string): string {
  return date.slice(0, 4)
}

/**
 * All person activity rows from affiliations (role in/out, stake changes), newest first.
 */
export function collectAllPersonActivity(
  affiliations: PersonAffiliation[]
): PersonActivityRow[] {
  const events = collectPersonActivityEvents(affiliations)
  events.sort((a, b) => compareIsoDatesDesc(a.date, b.date))
  return events
}

/**
 * Build the N most recent person activity rows from affiliations already in memory.
 * Includes role entries/exits and ownership stake changes between consecutive periods.
 */
export function collectPersonRecentActivity(
  affiliations: PersonAffiliation[],
  limit = DEFAULT_LIMIT
): PersonActivityRow[] {
  return collectAllPersonActivity(affiliations).slice(0, limit)
}

function collectPersonActivityEvents(
  affiliations: PersonAffiliation[]
): PersonActivityRow[] {
  const events: PersonActivityRow[] = []

  for (const aff of affiliations) {
    const companyCvr = aff.company.cvrNummer || ''
    const companyName = aff.company.navn || companyCvr
    const companyCountryCode = companyCountryFlagCode(aff.company)

    for (const role of aff.roles) {
      const roleLabel = formatPersonRoleLabel(role.function || role.organizationType)
      const organizationType = role.organizationType
      const stake = stakeFields(formatRoleStake(role))

      if (role.validFrom) {
        events.push({
          kind: 'in',
          date: role.validFrom,
          companyCvr,
          companyName,
          companyCountryCode,
          roleLabel,
          organizationType,
          ...stake,
          previousStakeLabel: null,
          previousStakeTitle: null,
        })
      }
      if (role.validTo) {
        events.push({
          kind: 'out',
          date: role.validTo,
          companyCvr,
          companyName,
          companyCountryCode,
          roleLabel,
          organizationType,
          ...stake,
          previousStakeLabel: null,
          previousStakeTitle: null,
        })
      }
    }

    const periods = aff.ownershipPeriods ?? []
    if (periods.length >= 2) {
      const sorted = [...periods].sort((a, b) =>
        (a.validFrom ?? '').localeCompare(b.validFrom ?? '')
      )
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1]
        const curr = sorted[i]
        if (!curr.validFrom) continue

        const prevKey = stakeFingerprint(
          prev.ownershipCapitalPercent,
          prev.ownershipCapitalPercentMax,
          prev.ownershipRegistrationUnderFive
        )
        const currKey = stakeFingerprint(
          curr.ownershipCapitalPercent,
          curr.ownershipCapitalPercentMax,
          curr.ownershipRegistrationUnderFive
        )
        if (prevKey === currKey) continue

        const orgType = curr.organizationType ?? prev.organizationType ?? 'EJERREGISTER'
        const nextStake = stakeFields(formatPeriodStake(curr))
        const prevStake = stakeFields(formatPeriodStake(prev))
        events.push({
          kind: 'stake_change',
          date: curr.validFrom,
          companyCvr,
          companyName,
          companyCountryCode,
          roleLabel: formatPersonRoleLabel(orgType),
          organizationType: orgType,
          stakeLabel: nextStake.stakeLabel,
          stakeTitle: nextStake.stakeTitle,
          previousStakeLabel: prevStake.stakeLabel,
          previousStakeTitle: prevStake.stakeTitle,
        })
      }
    }
  }

  return events
}

/** All activity dates for one affiliation (role in/out, stake changes). */
export function collectAffiliationActivityDates(aff: PersonAffiliation): string[] {
  const dates: string[] = []

  for (const role of aff.roles) {
    if (role.validFrom) dates.push(role.validFrom)
    if (role.validTo) dates.push(role.validTo)
  }

  const periods = aff.ownershipPeriods ?? []
  if (periods.length >= 2) {
    const sorted = [...periods].sort((a, b) =>
      (a.validFrom ?? '').localeCompare(b.validFrom ?? ''),
    )
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1]
      const curr = sorted[i]
      if (!curr.validFrom) continue

      const prevKey = stakeFingerprint(
        prev.ownershipCapitalPercent,
        prev.ownershipCapitalPercentMax,
        prev.ownershipRegistrationUnderFive,
      )
      const currKey = stakeFingerprint(
        curr.ownershipCapitalPercent,
        curr.ownershipCapitalPercentMax,
        curr.ownershipRegistrationUnderFive,
      )
      if (prevKey !== currKey) dates.push(curr.validFrom)
    }
  }

  return dates
}

export function latestAffiliationActivityDate(aff: PersonAffiliation): string | null {
  const dates = collectAffiliationActivityDates(aff)
  if (!dates.length) return null
  return dates.reduce((latest, d) => {
    const dKey = d.split('T')[0]
    const latestKey = latest.split('T')[0]
    return dKey > latestKey ? d : latest
  })
}
